// The background loader's order book: three tiers of asset keys, drained one tier at a
// time. Pure so `node --test` covers the re-prioritising rules; PreloadScene feeds batches
// to Phaser's loader.

// How many keys one loader run fetches. Small: whatever is in flight when a scene starts
// its own blocking load is aborted and re-queued (PreloadScene.yield), but the abort is not
// free and the pre-login stream must never hog a slow link — 4 files is the compromise.
export const BATCH_SIZE = 4;

export class PreloadQueue {
  /** `isLoaded(key)` answers whether the cache already has it. */
  constructor(isLoaded) {
    this._isLoaded = isLoaded;
    this._tiers = [];
    this._inFlight = null;
  }

  /** Replace every tier that has not started; a tier in flight finishes first. */
  setTiers(tiers) {
    this._tiers = tiers.map((t) => [...t]);
  }

  /** The next batch of not-yet-loaded keys from the next tier, marked in flight; [] when
   * nothing is left. Capped at BATCH_SIZE so a big tier (tier 2 is ~200 files) does not
   * hold the loader for its whole duration: `setTiers` from a prioritise() can replace the
   * remainder — pushed back as the new head of `_tiers` — between batches. */
  nextBatch() {
    if (this._inFlight) return [];
    while (this._tiers.length) {
      const tier = this._tiers.shift().filter((k) => !this._isLoaded(k));
      if (!tier.length) continue;
      const batch = tier.slice(0, BATCH_SIZE);
      const rest = tier.slice(BATCH_SIZE);
      if (rest.length) this._tiers.unshift(rest);
      this._inFlight = batch;
      return batch;
    }
    return [];
  }

  markDone(keys) {
    if (!this._inFlight) return;
    const done = new Set(keys);
    this._inFlight = this._inFlight.filter((k) => !done.has(k));
    if (!this._inFlight.length) this._inFlight = null;
  }

  /** Keys to fetch before anything else queued (a scene's own download of them failed). */
  pushFront(keys) {
    const fresh = keys.filter((k) => !this._isLoaded(k));
    if (fresh.length) this._tiers.unshift(fresh);
  }

  /** In-flight keys whose download was aborted (a scene needed the link): back to the head
   * of the queue so the next batch fetches them first — unless a `setTiers` in between
   * replaces the whole order, which is exactly what a prioritise() should do. */
  abandon(keys) {
    if (!this._inFlight) return;
    const back = this._inFlight.filter((k) => keys.includes(k));
    this.markDone(keys);
    if (back.length) this._tiers.unshift(back);
  }

  /** Count of not-yet-started, not-yet-loaded keys still queued in the tiers below the
   * current in-flight batch (which Phaser's own loader progress tracks separately). */
  get pending() {
    return this._tiers.reduce((n, t) => n + t.filter((k) => !this._isLoaded(k)).length, 0);
  }
}
